// src/services/itpService.ts
// Fetches and mutates ITP records from the Cloudflare Worker API (Neon database).
//
// Key endpoint: PATCH /api/itp/:id/witness
//   — records sign-off, sets status to PASSED/FAILED
//   — when ALL records for a job reach PASSED, Worker automatically sets
//     handoverPacks.itpComplete = true (Phase 3 TS09 workflow, built in Worker)
//   — response includes optional { handoverReadiness: { itpComplete: true } }
//     which Handover.tsx can use to show the readiness flag without a re-fetch.

import { api } from '../utils/api'

// ── API shape ──────────────────────────────────────────────────────────────
export interface ApiITPRecord {
  id: string
  jobId: string
  wiId: string | null
  wiStepId: string | null
  title: string
  description: string | null
  status: string       // PENDING | IN_PROGRESS | PASSED | FAILED
  holdType: string | null
  witnessedById: string | null
  witnessedAt: string | null
  witnessSignature: string | null
  result: string | null  // PASS | FAIL | CONDITIONAL
  notes: string | null
  createdAt: string
  updatedAt: string
}

// ── Frontend shape ─────────────────────────────────────────────────────────
export interface MappedITPRecord {
  id: string
  jobId: string
  wiId: string | null
  wiStepId: string | null
  title: string
  description: string | null
  status: string
  holdType: string | null
  witnessedById: string | null
  witnessedAt: string | null
  witnessSignature: string | null
  result: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  _fromNeon?: boolean
}

// ── Mapper ─────────────────────────────────────────────────────────────────
function mapITPRecord(raw: ApiITPRecord): MappedITPRecord {
  return {
    id:               raw.id,
    jobId:            raw.jobId,
    wiId:             raw.wiId             ?? null,
    wiStepId:         raw.wiStepId         ?? null,
    title:            raw.title,
    description:      raw.description      ?? null,
    status:           raw.status           ?? 'PENDING',
    holdType:         raw.holdType         ?? null,
    witnessedById:    raw.witnessedById    ?? null,
    witnessedAt:      raw.witnessedAt      ?? null,
    witnessSignature: raw.witnessSignature ?? null,
    result:           raw.result           ?? null,
    notes:            raw.notes            ?? null,
    createdAt:        raw.createdAt,
    updatedAt:        raw.updatedAt,
    _fromNeon:        true,
  }
}

// ── Read ───────────────────────────────────────────────────────────────────
export async function fetchITPRecords(params?: {
  jobId?: string
  status?: string
  holdType?: string
}): Promise<MappedITPRecord[]> {
  const qs = new URLSearchParams()
  if (params?.jobId)    qs.set('jobId',    params.jobId)
  if (params?.status)   qs.set('status',   params.status)
  if (params?.holdType) qs.set('holdType', params.holdType)
  const query = qs.toString() ? `?${qs.toString()}` : ''
  const { data } = await api.get<{ data: ApiITPRecord[] } | ApiITPRecord[]>(
    `/api/itp${query}`
  )
  const raw: ApiITPRecord[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapITPRecord)
}

// ── Witness sign-off (core action — triggers handover readiness) ───────────
export async function witnessITPRecord(
  id: string,
  body: {
    witnessedById: string
    result: 'PASS' | 'FAIL' | 'CONDITIONAL'
    witnessSignature?: string
    notes?: string
  }
): Promise<{
  record: MappedITPRecord
  handoverReadiness?: { itpComplete: boolean }
}> {
  const { data } = await api.patch<{
    data: ApiITPRecord
    handoverReadiness?: { itpComplete: boolean }
  }>(`/api/itp/${id}/witness`, body)
  return {
    record:            mapITPRecord((data as any).data),
    handoverReadiness: (data as any).handoverReadiness,
  }
}

// ── Create ─────────────────────────────────────────────────────────────────
export async function createITPRecord(body: {
  jobId: string
  title: string
  description?: string
  holdType?: string | null
  wiId?: string | null
  wiStepId?: string | null
  notes?: string | null
}): Promise<MappedITPRecord> {
  const { data } = await api.post<{ data: ApiITPRecord }>('/api/itp', body)
  return mapITPRecord((data as any).data)
}

// ── General update ─────────────────────────────────────────────────────────
export async function updateITPRecord(
  id: string,
  updates: Partial<{
    title: string
    description: string | null
    status: string
    holdType: string | null
    notes: string | null
  }>
): Promise<MappedITPRecord> {
  const { data } = await api.patch<{ data: ApiITPRecord }>(
    `/api/itp/${id}`,
    updates
  )
  return mapITPRecord((data as any).data)
}
